// Shared Pal types, constants and helpers. No data imports, so it's safe in client bundles.

export type Suitability = { type: string; level: number };
export type Skill = {
  level: number;
  name: string;
  type: string;
  cooldown: number;
  power: number;
  description: string;
};
export type Drop = { item: string; name: string; chance: number | null; min: number | null; max: number | null };
export type SpawnFlags = { day: boolean; night: boolean };

export type Pal = {
  id: number;
  /** URL slug, e.g. "quivern-botan" */
  key: string;
  /** Paldeck number as shown in game, e.g. "124B" */
  number: string;
  name: string;
  asset: string;
  image: string;
  wiki: string;
  types: string[];
  suitability: Suitability[];
  drops: string[];
  dropDetails: Drop[];
  aura: { name: string; description: string; tech: string | null };
  description: string;
  skills: Skill[];
  stats: {
    hp: number;
    attack: number;
    defense: number;
    workSpeed: number;
    stamina: number;
    speed: { walk: number; run: number; ride: number; transport: number; swim: number };
    food: number;
  };
  nocturnal: boolean;
  price: number;
  size: string;
  egg: string | null;
  breeding: {
    rank: number;
    /** game's tie-break priority: when two Pals are equally close, the higher one hatches */
    priority: number;
    breedable: boolean;
    /** can hatch from the breeding-power formula (otherwise only from a special combo) */
    child_eligble: boolean;
    male_probability: number;
  };
  spawn: { world: SpawnFlags; tree: SpawnFlags };
};

export type Combo = { a: string; b: string; child: string; aGender: "male" | "female" | null; bGender: "male" | "female" | null };

/** Slim record used by client-side features (see src/app/pals-index.json). */
export type PalIndex = Pick<Pal, "id" | "key" | "number" | "name" | "image" | "types" | "suitability" | "drops" | "size" | "breeding"> & {
  aura: string;
};

export const ELEMENTS = [
  "neutral",
  "fire",
  "water",
  "grass",
  "electric",
  "ice",
  "ground",
  "dark",
  "dragon",
] as const;

export const WORKS = [
  { type: "kindling", icon: "Kindling_Icon.webp" },
  { type: "watering", icon: "Watering_Icon.webp" },
  { type: "planting", icon: "Planting_Icon.webp" },
  { type: "generating_electricity", icon: "Generating_Electricity_Icon.webp" },
  { type: "handiwork", icon: "Handiwork_Icon.webp" },
  { type: "gathering", icon: "Gathering_Icon.webp" },
  { type: "lumbering", icon: "Lumbering_Icon.webp" },
  { type: "mining", icon: "Mining_Icon.webp" },
  { type: "medicine_production", icon: "Medicine_Production_Icon.webp" },
  { type: "cooling", icon: "Cooling_Icon.webp" },
  { type: "transporting", icon: "Transporting_Icon.webp" },
  { type: "farming", icon: "Farming_Icon.webp" },
] as const;

export const workIcon = (type: string) =>
  `/images/suitabilities/${WORKS.find((w) => w.type === type)?.icon ?? "Handiwork_Icon.webp"}`;

/** Element colours (text / chip background). */
export const ELEMENT_COLORS: Record<string, string> = {
  neutral: "#A8A878",
  fire: "#F08030",
  water: "#6890F0",
  grass: "#78C850",
  electric: "#F8D030",
  ice: "#98D8D8",
  ground: "#E0C068",
  dark: "#A48BC4",
  dragon: "#8B5CF8",
};

/** Short 3-letter element codes used on chips (Stitch design). */
export const ELEMENT_CODES: Record<string, string> = {
  neutral: "NEU",
  fire: "FIRE",
  water: "WTR",
  grass: "GRS",
  electric: "ELC",
  ice: "ICE",
  ground: "GRD",
  dark: "DRK",
  dragon: "DRG",
};

/** "generating_electricity" → "Generating Electricity", "elizabee's_staff" → "Elizabee's Staff" */
export const titleCase = (s: string) =>
  s
    .replace(/_/g, " ")
    .replace(/(^|\s)([a-z])/g, (_m, sp: string, c: string) => sp + c.toUpperCase());

/** item id → URL-safe file name ("elizabee's_staff" → "elizabee-s-staff") */
export const itemSlug = (drop: string) => drop.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export const palNumber = (p: { number: string }) => `#${p.number}`;

export const SIZE_LABEL: Record<string, string> = { xs: "Extra small", s: "Small", m: "Medium", l: "Large", xl: "Extra large" };

export const dropLabel = (d: Drop) => {
  const qty = d.min !== null && d.max !== null ? (d.min === d.max ? `×${d.min}` : `×${d.min}–${d.max}`) : "";
  return [qty, d.chance !== null && d.chance < 100 ? `${d.chance}%` : ""].filter(Boolean).join(" · ");
};
